import React, { useState, useEffect } from 'react';
import { Receipt, Plus, Download, Edit, Trash2, Filter } from 'lucide-react';
import API from '../../api';
import generateSalarySlipPDF from '../../utils/pdfUtils';
import GenerateSalarySlipModal from '../smallComponent/GenerateSalarySlipModal';
import MessageModal from '../smallComponent/MessageModal';

const salaryService = {
  async generateSalarySlip(salaryData) {
    try {
      const { data } = await API.post('/salary/generate', salaryData);
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error.response?.data?.message || error.message },
      };
    }
  },

  async updateSalarySlip(id, salaryData) {
    try {
      const { data } = await API.put(`/salary/${id}`, salaryData);
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error.response?.data?.message || error.message },
      };
    }
  },

  async deleteSalarySlip(id) {
    try {
      const { data } = await API.delete(`/salary/delete/${id}`);
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error.response?.data?.message || error.message },
      };
    }
  },

  async getSalarySlips(employeeId) {
    try {
      const endpoint = employeeId ? `/salary?employee_id=${employeeId}` : '/salary';
      const { data } = await API.get(endpoint);
      return { data: data.salary_slips, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error.response?.data?.message || error.message },
      };
    }
  }
};

const employeeService = {
  async getEmployees() {
    try {
      const { data } = await API.get('/employees');
      return { data: data.employees, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error.response?.data?.message || error.message },
      };
    }
  }
};

const SalaryManagement = () => {
  const [salarySlips, setSalarySlips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    month: '',
    year: '',
    basic_salary: '',
    allowances: '',
    deductions: '',
    days_worked: '',
    total_days: '',
    overtime_hours: ''
  });

  const [editingSlip, setEditingSlip] = useState(null);
  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
  });
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [salaryResult, employeeResult] = await Promise.all([
        salaryService.getSalarySlips(),
        employeeService.getEmployees()
      ]);

      if (salaryResult.error) throw new Error(salaryResult.error.message);
      if (employeeResult.error) throw new Error(employeeResult.error.message);

      setSalarySlips(salaryResult.data || []);
      setEmployees(employeeResult.data || []);
    } catch (error) {
      alert(error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (title, message, type = 'info') => {
    setMessageModal({ show: true, title, message, type });
  };

  const handleGenerateSalarySlip = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      employee_id: formData.employee_id,
      month: parseInt(formData.month),
      year: parseInt(formData.year),
      basic_salary: parseFloat(formData.basic_salary),
      allowances: parseFloat(formData.allowances) || 0,
      deductions: parseFloat(formData.deductions) || 0,
      days_worked: parseFloat(formData.days_worked),
      total_days: parseInt(30), // Fixed to 30 as per requirements
      overtime_hours: parseFloat(formData.overtime_hours) || 0
    };

    try {
      let res;
      if (editingSlip) {
        res = await salaryService.updateSalarySlip(editingSlip.id, payload);
      } else {
        res = await salaryService.generateSalarySlip(payload);
      }

      if (res.error) {
        if (res.error.message.includes('already exists')) {
          showMessage('Duplicate Entry', 'Salary slip already exists for this employee and month.', 'warning');
        } else {
          showMessage('Error', res.error.message, 'error');
        }
        return;
      }

      showMessage('Success', editingSlip ? 'Salary slip updated successfully!' : 'Salary slip generated successfully!', 'success');
      setShowGenerateForm(false);
      setEditingSlip(null);
      setFormData({
        employee_id: '', month: '', year: '', basic_salary: '',
        allowances: '', deductions: '', days_worked: '', total_days: '', overtime_hours: ''
      });
      fetchData();
    } catch (error) {
      showMessage('Failed', 'An unexpected error occurred.', 'error');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'employee_id') {
      const emp = employees.find(e => e.id.toString() === value.toString());
      if (emp) {
        setFormData(prev => ({ ...prev, basic_salary: emp.salary?.toString() || '' }));
      }
    }
  };

  const downloadSalarySlip = (slip) => {
    generateSalarySlipPDF(slip);
  };

  const handleEditClick = (slip) => {
    setEditingSlip(slip);
    setFormData({
      employee_id: slip.employee_id.toString(),
      month: slip.month.toString(),
      year: slip.year.toString(),
      basic_salary: slip.basic_salary.toString(),
      allowances: slip.allowances?.toString() || '',
      deductions: slip.deductions?.toString() || '',
      days_worked: slip.days_worked.toString(),
      total_days: slip.total_days.toString(),
      overtime_hours: slip.overtime_hours?.toString() || '0',
    });
    setShowGenerateForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm('Are you sure you want to delete this salary slip?')) return;
    try {
      const { error } = await salaryService.deleteSalarySlip(id);
      if (error) {
        showMessage('Error', error.message, 'error');
        return;
      }
      showMessage('Success', 'Salary slip deleted successfully!', 'success');
      fetchData();
    } catch (error) {
      showMessage('Error', 'Failed to delete salary slip.', 'error');
      console.error(error);
    }
  };

  // Filtered salary slips
  const filteredSlips = salarySlips.filter((slip) => {
    return (
      (filterMonth === '' || slip.month.toString() === filterMonth) &&
      (filterYear === '' || slip.year.toString() === filterYear)
    );
  });

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Receipt className="w-6 h-6 text-green-600" />
          <h1 className="text-xl font-bold">Salary Management</h1>
        </div>
        <button
          onClick={() => {
            setShowGenerateForm(true);
            setEditingSlip(null);
            setFormData({
              employee_id: '', month: '', year: '', basic_salary: '',
              allowances: '', deductions: '', days_worked: '', total_days: '', overtime_hours: ''
            });
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" /> <span>Generate Salary Slip</span>
        </button>
      </div>

      {/* Filter Section */}
      <div className="bg-white shadow p-4 rounded-lg flex items-center space-x-4">
        <Filter className="w-5 h-5 text-gray-500" />
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="border rounded p-2"
        >
          <option value="">All Months</option>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={(i + 1).toString()}>
              {new Date(0, i).toLocaleString('default', { month: 'long' })}
            </option>
          ))}
        </select>

        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          className="border rounded p-2"
        >
          <option value="">All Years</option>
          {[2022, 2023, 2024, 2025].map((year) => (
            <option key={year} value={year.toString()}>{year}</option>
          ))}
        </select>

        <button
          onClick={() => {
            setFilterMonth('');
            setFilterYear('');
          }}
          className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
        >
          Reset
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left font-medium text-gray-500">Employee</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500">Period</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500">Basic Salary</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500">Net Salary</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredSlips.length > 0 ? (
              filteredSlips.map((slip) => (
                <tr key={slip.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{slip.full_name}</div>
                    <div className="text-gray-500 text-xs">{slip.emp_id}</div>
                  </td>
                  <td className="px-6 py-4">{slip.month}/{slip.year}</td>
                  <td className="px-6 py-4">₹{slip.basic_salary}</td>
                  <td className="px-6 py-4 text-green-600 font-semibold">₹{slip.net_salary}</td>
                  <td className="flex px-6 py-4 space-x-2">
                    <button
                      onClick={() => downloadSalarySlip(slip)}
                      className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      title="Download Salary Slip"
                    >
                      <Download className="w-4 h-4" /> <span>Download</span>
                    </button>

                    <button
                      onClick={() => handleEditClick(slip)}
                      className="text-yellow-600 hover:text-yellow-800 flex items-center ms-3"
                      title="Edit Salary Slip"
                    >
                      <Edit className="w-4 h-4" /> <span>Edit</span>
                    </button>

                    <button
                      onClick={() => handleDeleteClick(slip.id)}
                      className="text-red-600 hover:text-red-800 flex items-center ms-3"
                      title="Delete Salary Slip"
                    >
                      <Trash2 className="w-4 h-4" /> <span>Delete</span>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-gray-500">
                  No salary slips found for the selected filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(showGenerateForm) && (
        <GenerateSalarySlipModal
          show={showGenerateForm}
          onClose={() => {
            setShowGenerateForm(false);
            setEditingSlip(null);
          }}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleGenerateSalarySlip}
          employees={employees}
          submitting={submitting}
        />
      )}

      <MessageModal
        show={messageModal.show}
        onClose={() => setMessageModal(prev => ({ ...prev, show: false }))}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
      />
    </div>
  );
};

export default SalaryManagement;
